import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import Input from "../components/form/Input";
import ButtonSecondary from "../components/buttons/ButtonSecondary";
import { Hourglass } from "react-loader-spinner";
import "react-toastify/dist/ReactToastify.css";
import useAuthApi from "../hooks/api/useAuthApi";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const { forgotPassword } = useAuthApi();

  const handleChange = (e) => {
    setEmail(e.target.value);
  };

  const validateEmail = () => {
    if (!email || email.trim() === "") {
      toast.error("Email is required");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateEmail()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await forgotPassword({
        email: email,
      });

      if (response && response.status === 200) {
        toast.success("Password reset link sent to your email!");
        setEmailSent(true);

        setTimeout(() => {
          navigate("/login");
        }, 3000);
      }
    } catch (error) {
      console.error("Forgot password error:", error);

      if (error.response) {
        const { status, data } = error.response;

        if (status === 404) {
          toast.error("No account found with this email address");
        } else if (status === 422) {
          if (data && data.errors) {
            Object.values(data.errors).forEach((errorArray) => {
              if (Array.isArray(errorArray)) {
                errorArray.forEach((msg) => toast.error(msg));
              }
            });
          } else {
            toast.error(data.error || "Validation error");
          }
        } else if (status === 429) {
          toast.error("Too many attempts. Please try again later.");
        } else {
          toast.error(data.error || "An error occurred. Please try again.");
        }
      } else if (error.request) {
        toast.error("Unable to connect to server. Please check your internet connection.");
      } else {
        toast.error("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card-login bg-white rounded-lg mobXS:mx-6 tabMD:mx-6 my-10 border desktopXl:w-1/4 desktop:w-1/2 desktop:mx-auto">
      <h6 className="text-h6 text-dark4 text-center py-12 px-8">
        Forgot your password?
      </h6>

      {!emailSent && (
        <p className="text-dark6 text-bodyMM text-center px-8 pb-6">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      )}

      {isLoading ? (
        <div className="flex flex-row justify-center items-center left-0 right-0 mx-auto pb-20">
          <Hourglass
            visible={true}
            height="40"
            width="40"
            animationDuration="2"
            ariaLabel="hourglass-loading"
            wrapperStyle={{}}
            wrapperClass=""
            colors={["#FF5400", "#FF5400"]}
          />
        </div>
      ) : emailSent ? (
        <div className="px-8 pb-12">
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <p className="text-green-800 text-bodyMM text-center">
              ✓ Password reset link has been sent to your email!
            </p>
          </div>
          <p className="text-dark6 text-bodyMS text-center mb-4">
            Check your inbox and follow the instructions to reset your password.
          </p>
          <p className="text-dark6 text-bodyMS text-center">
            Redirecting to login page...
          </p>
        </div>
      ) : (
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-px desktop:w-full flex justify-center">
            <div className="mobXS:w-full tabMD:w-1/2 desktop:w-full tabMD:items-center">
              <Input
                handleChange={handleChange}
                value={email}
                labelText="Your email"
                labelFor="email-address"
                id="email-address"
                name="email"
                type="email"
                isRequired={true}
                placeholder="Your email"
                customClass="rounded-lg"
                disabled={isLoading}
              />
            </div>
          </div>

          <div className="flex flex-col gap-y-6 px-6 mobXS:items-start mobXS:justify-self-start tabMD:items-center tabMD:justify-self-center">
            <ButtonSecondary
              text={isLoading ? "Sending..." : "Send reset link"}
              type="submit"
              bgColor="bg-secondary"
              color="text-white"
              padding="mobXS:py-[10px] mobXS:px-[24px] tabMD:py-[10px] tabMD:px-[24px]"
              customClass="rounded-[25px]"
              fontsize="text-bodyMR"
              width="mobXS:w-full tabMD:w-1/2 desktop:w-full"
              disabled={isLoading}
            />
          </div>

          <div className="flex justify-center pb-10">
            <a
              className="text-primary text-bodyMM hover:underline"
              href="/login"
            >
              Back to login
            </a>
          </div>
        </form>
      )}

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default ForgotPasswordPage;